import { useEffect, useMemo, useState } from 'react';
import { useEmployer } from '../context/EmployerContext';
import { useDrawer } from '../../drawer/DrawerContext';
import DefinitionList from '../../icib-admin/components/DefinitionList';
import LifeRail from '../../icib-admin/components/LifeRail';
import Pagination, { usePagination } from '../../icib-admin/components/Pagination';
import { fmtDate, fmtDT } from '../../icib-admin/utils/format';

/* EMP-01 — "The system shall allow employer administrators to view all
   employees covered by their employee benefits scheme." Same split as
   ICIB-01, employer-scoped (BrandonUserStoryAPI.md EMP-01, README §4.4):
   a lightweight list across the employer_admin's own group (EMP-01a, GET
   .../employees/summary — employee_id + user only, no form_data/
   enrolments), and a per-person drill-down (EMP-01b, GET .../employees/{id})
   for the rest, fetched on demand when a row is opened.

   Read-only, unlike ICIB-01's EmployeesView — PATCH .../form-data is
   icib_admin-only (README §4.4), so there's no override form here, just
   the submitted form_data and enrolments for reference. */
function EmployeesView() {
  const {
    status, groupId, curGroup, employeesIn, employeeDetails,
    employeesStatus, employeesError, loadEmployees,
  } = useEmployer();
  const { openDrawer } = useDrawer();
  const [q, setQ] = useState('');

  useEffect(() => { loadEmployees(groupId); }, [groupId, loadEmployees]);

  const employees = useMemo(() => {
    const list = groupId ? employeesIn(groupId) : [];
    const needle = q.trim().toLowerCase();
    if (!needle) return list;
    return list.filter((emp) => `${emp.user?.first_name || ''} ${emp.user?.last_name || ''} ${emp.user?.email || ''}`.toLowerCase().includes(needle));
  }, [groupId, employeesIn, q]);
  const { page, setPage, pageCount, pageItems: pagedEmployees, total } = usePagination(employees);

  function open(emp) {
    openDrawer(
      `${emp.user?.first_name || ''} ${emp.user?.last_name || ''}`.trim() || emp.user?.email,
      `EMP-01b · ${emp.id}`,
      <EmployeeDrawer gid={groupId} emp={emp} onGone={() => loadEmployees(groupId)} />
    );
  }

  if (status === 'loading') return <div className="card"><div className="empty"><b>Loading…</b></div></div>;

  return (
    <>
      <div className="eyebrow">Membership register</div>
      <div className="page-h"><h2>Employees</h2></div>
      <p className="page-sub">Everyone covered under {curGroup()?.name || 'your employee benefits scheme'} — who is in it. Open a person for their enrolments and submitted details.</p>

      {employeesError && <div className="note bad" style={{ marginBottom: 14 }}><b>Couldn't load the register</b>{employeesError}</div>}

      {!groupId ? (
        <div className="card"><div className="empty"><b>No company assigned</b>Your account isn't currently assigned to a company.</div></div>
      ) : (
        <>
          <div className="filterbar">
            <input type="search" placeholder="Search name or email…" value={q} onChange={(e) => setQ(e.target.value)} />
            <span className="spacer" style={{ flex: 1 }} />
            <span className="ep">GET /companies/{'{company_id}'}/employees/summary</span>
          </div>

          <div className="card">
            {employeesStatus === 'loading' || employeesStatus === 'idle' ? (
              <div className="empty"><b>Loading register…</b></div>
            ) : employees.length ? (
              <div className="tbl-wrap">
                <table>
                  <thead><tr><th>Person</th><th></th></tr></thead>
                  <tbody>
                    {pagedEmployees.map((emp) => {
                      const detail = employeeDetails[emp.id];
                      return (
                        <tr className="clickable" key={emp.id} onClick={() => open(emp)}>
                          <td>
                            <span className="strong">{emp.user?.first_name} {emp.user?.last_name}</span><br />
                            <span className="mono dim">{emp.user?.email}</span>
                          </td>
                          <td>
                            {detail ? (detail.enrolments || []).length
                              ? <span className="dim">{detail.enrolments.length} enrolment(s)</span>
                              : <span className="dim">No enrolments</span>
                              : <button className="btn sm" onClick={(e) => { e.stopPropagation(); open(emp); }}>Open</button>}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            ) : (
              <div className="empty"><b>No matches</b>{q ? 'Try a different search.' : 'No one is enrolled in this company yet.'}</div>
            )}
            <Pagination page={page} setPage={setPage} pageCount={pageCount} total={total} />
          </div>
        </>
      )}
    </>
  );
}

function EmployeeDrawer({ gid, emp, onGone }) {
  const { employeeDetails, loadEmployeeDetail, groupProductsIn } = useEmployer();
  // loadEmployeeDetail isn't stable across renders — key off emp.id alone,
  // same pattern as ICIB-01's EmployeeDrawer.
  // Set when the detail fetch fails (e.g. 404 because the employee was
  // removed after the list loaded). Shown instead of an endless "Loading…";
  // on 404 the list is reloaded (onGone) so the stale row disappears.
  const [loadError, setLoadError] = useState(null);
  useEffect(() => {
    let live = true;
    setLoadError(null);
    loadEmployeeDetail(gid, emp.id).then((res) => {
      if (!live || !res?.error) return;
      setLoadError(res);
      if (res.error === 'not_found') onGone?.();
    });
    return () => { live = false; };
  }, [gid, emp.id]); // eslint-disable-line react-hooks/exhaustive-deps
  // groupProducts (GET /companies/{gid}/products, README §4.6) now loads as
  // part of EmployerContext's shared refresh() batch for whichever group
  // is selected — no per-view call needed here to resolve a product's name.
  const detail = employeeDetails[emp.id];
  const products = gid ? groupProductsIn(gid) : [];

  return (
    <>
      <div className="sec-h" style={{ marginTop: 0 }}><h3>Contact</h3></div>
      <DefinitionList rows={[
        ['Email', emp.user?.email || '—'],
        ['Date of birth', fmtDate(emp.user?.dob)],
        ['Phone', emp.user?.phone || '—'],
        ['Address', emp.user?.address || '—'],
        ['Gender', emp.user?.gender || '—'],
      ]} />

      {loadError ? (
        <div className="note bad" style={{ marginTop: 14 }}><b>{loadError.error === 'not_found' ? 'This employee no longer exists' : "Couldn't load this employee"}</b>{loadError.message || 'Please reload the list.'}</div>
      ) : !detail ? (
        <div className="empty"><b>Loading…</b></div>
      ) : (
        <>
          {/* Same shape as ICIB-01b (BrandonUserStoryAPI.md §EMP-01b) —
              product_id/status/invited_at plus reason/effective_date/
              removal_reason/removal_effective_date per enrolment. Product
              name resolved from GET /companies/{gid}/products (README §4.6 —
              employer_admin can call the list, just active-only, no
              ?include_inactive) — same pattern LeaversView already uses;
              falls back to the raw id if the product isn't in that list
              (e.g. removed, which employer_admin never sees per §4.6). */}
          <div className="sec-h" style={{ marginTop: 0 }}><h3>Enrolments</h3></div>
          {(detail.enrolments || []).length ? detail.enrolments.map((en) => (
            <div className="card card-b" key={en.id} style={{ marginBottom: 10 }}>
              <div className="card-h" style={{ padding: 0, border: 0, marginBottom: 8 }}>
                <b>{products.find((p) => p.id === en.product_id)?.name || <span className="mono">{en.product_id}</span>}</b>
                <span className="spacer" />
                <LifeRail status={en.status} withLabel />
              </div>
              <DefinitionList rows={[
                ['Invited', fmtDT(en.invited_at)],
                ['Employment effective date', fmtDate(en.effective_date)],
                ['Reason', en.reason || '—'],
                ['Removal reason', en.removal_reason || '—'],
                ['Removal effective date', fmtDate(en.removal_effective_date)],
              ]} />
            </div>
          )) : <div className="empty"><b>No enrolments</b></div>}

          <div className="sec-h"><h3>Company-Specific extra details</h3></div>
          {Object.keys(detail.form_data || {}).length ? (
            <DefinitionList rows={Object.entries(detail.form_data).map(([k, v]) => [k.replace(/_/g, ' '), String(v)])} />
          ) : (
            <div className="empty"><b>No company-specific extra details</b></div>
          )}
        </>
      )}
    </>
  );
}

export default EmployeesView;
